import { XMarkIcon } from '@heroicons/react/20/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';
import { FourOhFour } from '../../components/404';
import { LinkButton, PrimaryButton, SmallSecondaryButton } from '../../components/buttons';
import {
  Checkbox,
  Field,
  FieldDescription,
  Input,
  Row,
  Section,
  Select,
} from '../../components/forms';
import { Loading } from '../../components/loading';
import { Page } from '../../components/page';
import { Spinner } from '../../components/spinner';
import { AlertContext } from '../../contexts/alert-context';
import {
  type ReorderWeeks,
  reorderWeekOptions,
  type ThresholdWeeks,
  thresholdWeekOptions,
} from '../../data/order-settings';
import {
  isDisabledByBounce,
  isGloballySuppressed,
  lastBounceReason,
} from '../../data/representative';
import type { Store } from '../../data/store';
import { type Vendor, VendorFormSchema } from '../../data/vendor';
import { formatShortDate } from '../../helpers/format-date';
import { SendMethodLabels } from '../../helpers/send-method-labels';
import {
  useReenableRepresentative,
  useVendor,
  useVendorIntegrations,
  useVendors,
} from '../../hooks/vendors';
import { storeLayout, vendorEditRoute } from '../../route-apis';

// small wrapper component to preload the vendor and store data
export function EditVendor() {
  const { store } = storeLayout.useRouteContext();
  const { vendorId } = vendorEditRoute.useParams();
  const { vendor, loading } = useVendor(vendorId);

  if (loading) {
    return <Loading />;
  }

  if (!vendor) {
    return <FourOhFour />;
  }

  return <EditVendorPopulated vendor={vendor} store={store} />;
}

// What the form holds is the schema's INPUT, not its output — VendorFormSchema
// still fixes up `invoice_formats` and `updated_at` on the way in, and neither
// is editable here. useForm's third generic is the transformed value, so form
// state stays honest about what it holds while onSubmit receives a parsed
// Vendor.
type VendorFormValues = z.input<typeof VendorFormSchema>;

function EditVendorPopulated({ vendor, store }: { vendor: Vendor; store: Store }) {
  const { addErrorAlert, addSuccessAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const { updateVendor } = useVendors();

  const integrations = useVendorIntegrations();

  const reenableMutation = useReenableRepresentative(store.id, vendor.vendor_id);

  // functions to build form returned by useForm() hook
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    formState,
  } = useForm<VendorFormValues, unknown, Vendor>({
    resolver: zodResolver(VendorFormSchema),
    defaultValues: vendor,
  });

  const selectedNormalizedName = useWatch({ control, name: 'normalized_name' });
  const selectedIntegration = useMemo(
    () => integrations.find((i) => i.normalized_name === selectedNormalizedName),
    [integrations, selectedNormalizedName]
  );
  const availableSendMethods = selectedIntegration?.ordering.send_methods ?? ['email'];

  // Both dropdowns lead with an empty-value option, so clearing the field is a
  // normal choice rather than something the user has to reach for a clear
  // button to do — Select is not isClearable.
  const integrationOptions = useMemo(
    () => [
      { value: '', label: 'No Integration' },
      ...integrations.map(({ normalized_name, label }) => ({ value: normalized_name, label })),
    ],
    [integrations]
  );

  const sendMethodOptions = useMemo(
    () => [
      { value: '', label: 'No Default' },
      ...availableSendMethods.map((method) => ({
        value: method,
        label: SendMethodLabels[method] || method,
      })),
    ],
    [availableSendMethods]
  );

  // When the user changes the integration, apply the new default send method.
  // Skip on mount so we don't overwrite the vendor's saved preference.
  const mountedNormalizedName = useRef(vendor.normalized_name);
  useEffect(() => {
    if (selectedNormalizedName === mountedNormalizedName.current) {
      return;
    }

    setValue(
      'order_settings.defaults.send_method',
      selectedIntegration?.ordering.default_send_method ?? 'email'
    );
  }, [selectedNormalizedName, selectedIntegration, setValue]);

  const onSubmit = useCallback(
    (data: Vendor) => {
      const updatedVendor = {
        ...vendor,
        ...data,
      };

      updateVendor(updatedVendor)
        .then(() => {
          addSuccessAlert('Vendor updated', { keepAfterRouteChange: true });
          navigate({
            to: '/stores/$storeId/vendors',
            params: { storeId: store.id },
          });
        })
        .catch(addErrorAlert);
    },
    [updateVendor, store, vendor, navigate, addSuccessAlert, addErrorAlert]
  );

  const {
    fields: representativeFields,
    append: appendRepresentative,
    remove: removeRepresentative,
  } = useFieldArray({
    control,
    name: 'order_settings.representatives',
  });

  const addRepresentative = useCallback(() => {
    appendRepresentative({
      first_name: '',
      last_name: '',
      division: '',
      email: '',
      send_order: true,
      last_permanent_bounce: null,
      bounce_history: [],
    });
  }, [appendRepresentative]);

  return (
    <Page title={vendor.name}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        onReset={() => reset()}
        className="flex flex-col gap-4 w-[32rem]"
      >
        <Field label="Disabled" name="disabled" errorMessage={errors.disabled?.message}>
          <Checkbox {...register('disabled')} invalid={!!errors.disabled} />
        </Field>
        <Field
          label="Vendor Integration"
          name="normalized_name"
          errorMessage={errors.normalized_name?.message}
        >
          <Controller
            control={control}
            name="normalized_name"
            render={({ field }) => (
              <Select<string>
                value={field.value ?? ''}
                onChange={field.onChange}
                options={integrationOptions}
                isDisabled={integrations.length === 0}
              />
            )}
          />
        </Field>
        {integrations.length === 0 && (
          <FieldDescription>
            No vendor integrations are configured for this store yet.
          </FieldDescription>
        )}
        <Section>Order Settings</Section>
        <FieldDescription>
          Based on the last <span className="underline">30 days of sales</span>, when current
          inventory would last less than <span className="font-bold">QOH Threshold Weeks</span>,
          automatically reorder enough inventory to last{' '}
          <span className="font-bold">Reorder Weeks</span>.
        </FieldDescription>
        {/*
          Dropdowns rather than number inputs, matching the order edit
          popover — see thresholdWeekOptions. Controller, not register:
          Select here is our react-select wrapper, which has no native input for a ref to
          bind to.
        */}
        <Field
          label="QOH Threshold Weeks"
          name="qoh_threshold_weeks"
          errorMessage={errors.order_settings?.defaults?.qoh_threshold_weeks?.message}
        >
          <Controller
            control={control}
            name="order_settings.defaults.qoh_threshold_weeks"
            render={({ field }) => (
              <Select<ThresholdWeeks>
                value={field.value}
                onChange={field.onChange}
                options={thresholdWeekOptions}
                className="w-24"
              />
            )}
          />
        </Field>
        <Field
          label="Reorder Weeks"
          name="reorder_weeks"
          errorMessage={errors.order_settings?.defaults?.reorder_weeks?.message}
        >
          <Controller
            control={control}
            name="order_settings.defaults.reorder_weeks"
            render={({ field }) => (
              <Select<ReorderWeeks>
                value={field.value}
                onChange={field.onChange}
                options={reorderWeekOptions}
                className="w-24"
              />
            )}
          />
        </Field>
        <Field
          label="Default Send Method"
          name="send_method"
          errorMessage={errors.order_settings?.defaults?.send_method?.message}
        >
          <Controller
            control={control}
            name="order_settings.defaults.send_method"
            render={({ field }) => (
              <Select<string>
                value={field.value ?? ''}
                onChange={field.onChange}
                options={sendMethodOptions}
              />
            )}
          />
        </Field>

        <Section>Representatives</Section>
        <div className="flex flex-col p-4 bg-gray-100 rounded-sm border border-gray-200 gap-2">
          {representativeFields.map((field, index) => (
            <div key={field.id} className="bg-white p-2 rounded-sm">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => removeRepresentative(index)}
                  className="p2 border border-red-500 rounded-sm text-red-500 hover:bg-red-100 hover:text-red-700"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              {(() => {
                const rep = vendor.order_settings.representatives[index];

                if (!rep || !isDisabledByBounce(rep)) {
                  return null;
                }

                const bounceDate = rep.last_permanent_bounce
                  ? ` · bounced ${formatShortDate(rep.last_permanent_bounce)}`
                  : '';
                const reason = lastBounceReason(rep);
                const globallyBlocked = isGloballySuppressed(rep);

                if (globallyBlocked) {
                  return (
                    <div className="mt-2 mb-2 rounded bg-status-danger-soft p-3 text-sm text-status-danger">
                      <p className="font-semibold">
                        {reason}
                        {bounceDate}
                      </p>
                      <p className="mt-1">
                        Our email provider has globally blocked this address. Update the email to a
                        working one to recover.
                      </p>
                    </div>
                  );
                }

                const isPendingThisEmail =
                  reenableMutation.isPending && reenableMutation.variables === rep.email;

                return (
                  <div className="mt-2 mb-2 rounded bg-status-attention-soft p-3 text-sm text-status-attention">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-semibold">
                        {reason}
                        {bounceDate}
                      </p>
                      <SmallSecondaryButton
                        type="button"
                        disabled={isPendingThisEmail}
                        onClick={() =>
                          reenableMutation.mutate(rep.email, {
                            onError: (err: Error) => addErrorAlert(err.message, { cause: err }),
                          })
                        }
                      >
                        {isPendingThisEmail ? (
                          <>
                            <Spinner sizeClass="mr-1 h-3 w-3" />
                            Re-enabling…
                          </>
                        ) : (
                          'Re-enable this address'
                        )}
                      </SmallSecondaryButton>
                    </div>
                  </div>
                );
              })()}
              <Field
                label="Email"
                name={`order_settings.representatives.${index}.email`}
                errorMessage={errors.order_settings?.representatives?.[index]?.email?.message}
              >
                <Input
                  {...register(`order_settings.representatives.${index}.email`)}
                  placeholder="jane.doe@example.com"
                  type="email"
                  invalid={!!errors.order_settings?.representatives?.[index]?.email?.message}
                />
              </Field>
              <Row>
                <Field
                  label="First Name"
                  name={`order_settings.representatives.${index}.first_name`}
                  errorMessage={
                    errors.order_settings?.representatives?.[index]?.first_name?.message
                  }
                >
                  <Input
                    {...register(`order_settings.representatives.${index}.first_name`)}
                    placeholder="Jane"
                    invalid={!!errors.order_settings?.representatives?.[index]?.first_name?.message}
                  />
                </Field>
                <Field
                  label="Last Name"
                  name={`order_settings.representatives.${index}.last_name`}
                  errorMessage={errors.order_settings?.representatives?.[index]?.last_name?.message}
                >
                  <Input
                    {...register(`order_settings.representatives.${index}.last_name`)}
                    placeholder="Doe"
                    invalid={!!errors.order_settings?.representatives?.[index]?.last_name?.message}
                  />
                </Field>
              </Row>
              <Row>
                <Field
                  label="Division"
                  name={`order_settings.representatives.${index}.division`}
                  errorMessage={errors.order_settings?.representatives?.[index]?.division?.message}
                >
                  <Input
                    {...register(`order_settings.representatives.${index}.division`)}
                    placeholder="Sales"
                    invalid={!!errors.order_settings?.representatives?.[index]?.division?.message}
                  />
                </Field>
                <Field
                  label="Send Order"
                  name={`order_settings.representatives.${index}.send_order`}
                  errorMessage={
                    errors.order_settings?.representatives?.[index]?.send_order?.message
                  }
                >
                  <Checkbox
                    {...register(`order_settings.representatives.${index}.send_order`)}
                    invalid={!!errors.order_settings?.representatives?.[index]?.send_order?.message}
                  />
                </Field>
              </Row>
            </div>
          ))}
          <div className="flex justify-center pt-2">
            <SmallSecondaryButton type="button" onClick={addRepresentative}>
              Add Representative
            </SmallSecondaryButton>
          </div>
        </div>

        <div className="flex gap-2 justify-center pt-4">
          <PrimaryButton type="submit" disabled={formState.isSubmitting}>
            {formState.isSubmitting && <Spinner />}
            Update
          </PrimaryButton>
          <LinkButton to="/stores/$storeId/vendors" params={{ storeId: store.id }}>
            Cancel
          </LinkButton>
        </div>
      </form>
    </Page>
  );
}
